// Invisible R3F component that pumps `gl.info` into the module-level
// `renderStats` singleton every frame. Mount once inside <Canvas>.

import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';

import { renderStats } from './renderStats.js';

const FPS_WINDOW_MS = 1000;

export function RenderStatsCollector() {
  // Lightweight FPS smoothing — track frame timestamps in a small ring.
  const frameTimes = useRef<number[]>([]);
  const lastFpsCompute = useRef<number>(0);
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    // The post stack renders the scene and then a chain of fullscreen passes, and
    // three.js resets the render counters at the START of every `gl.render()` call.
    // Read per frame, the counters therefore describe the LAST pass (one
    // fullscreen quad — "1 draw call, 0 triangles") instead of the frame. Take
    // the counters over: turn three's auto-reset off and reset them ourselves
    // once per frame, so they add up over the whole frame (scene + every pass).
    gl.info.autoReset = false;
    return () => {
      gl.info.autoReset = true;
    };
  }, [gl]);

  useEffect(() => {
    return () => {
      renderStats.drawCalls = 0;
      renderStats.triangles = 0;
      renderStats.geometries = 0;
      renderStats.textures = 0;
      renderStats.programs = 0;
      renderStats.fps = 0;
    };
  }, []);

  useFrame(() => {
    const info = gl.info;
    renderStats.drawCalls = info.render.calls;
    renderStats.triangles = info.render.triangles;
    renderStats.geometries = info.memory.geometries;
    renderStats.textures = info.memory.textures;
    renderStats.programs = info.programs?.length ?? 0;
    // Start the next frame's tally.
    info.reset();

    // FPS smoothing: push the current time, drop anything older than the window.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const times = frameTimes.current;
    times.push(now);
    const cutoff = now - FPS_WINDOW_MS;
    while (times.length > 0 && (times[0] ?? now) < cutoff) times.shift();

    // Only recompute fps at most every ~250ms to avoid jitter on the display.
    if (now - lastFpsCompute.current >= 250) {
      lastFpsCompute.current = now;
      const span = times.length >= 2 ? (times[times.length - 1] ?? now) - (times[0] ?? now) : 0;
      renderStats.fps = span > 0 ? Math.round((times.length * 1000) / span) : 0;
    }
  });

  return null;
}
